import React, {useContext, useEffect, useMemo, useState} from "react";
import {InboxContext} from "routes/MessagesPage";
import ChipRow from "../../../../../connectui/component/display/chip/ChipRow";
import ChipText from "../../../../../connectui/component/display/chip/ChipText";
import XChipBase from "../../../../../connectui/component/display/chip/base/XChipBase";
import ChipBase from "../../../../../connectui/component/display/chip/base/ChipBase";
import Skeleton from "../../../../../connectui/component/display/Skeleton";
import {useTheme} from "../../../../../connectui/theme/ThemeProvider";
import ContextMenu from "../../../../../connectui/component/input/context/ContextMenu";
import {getCustomFields} from "../../../../../redux/actions/settingsActions";
import {useDispatch} from "react-redux";
import Container from "../../../../../connectui/component/layout/Container";
import TextInput from "../../../../../connectui/component/input/text/type/TextInput";
import FlexContainer from "../../../../../connectui/component/layout/FlexContainer";
import SvgIcon from "../../../../../connectui/component/display/SvgIcon";
import ICONS from "../../../../../connectui/icons/iconMap";
import {buildStyles} from "../../../../../connectui/component/buildStyles";
import ListComponent from "../../../../../connectui/component/layout/list/ListComponent";
import Text from "../../../../../connectui/component/display/Text";
import ListItemContainer from "../../../../../connectui/component/layout/list/ListItemContainer";
import CheckBox from "../../../../../connectui/component/input/CheckBox";
import OutlinedInputBase from "../../../../../connectui/component/input/text/OutlinedInputBase";
import {useTranslation} from "react-i18next";
import ButtonText from "../../../../../connectui/component/input/button/ButtonText";
import OutlinedButtonBase from "../../../../../connectui/component/input/button/base/OutlinedButtonBase";

const useStyles = (theme) =>
  buildStyles({
    icon: {
      width: "19px",
      height: "19px",
    },
    addIcon: {
      width: "16px",
      height: "16px",
    },
    menuContainer: {
      padding: theme.spacing(2, 2),
      minWidth: "200px",
    },
    searchIcon: {
      width: "15px",
      height: "15px",
      color: theme.colors.textPrimary,
      marginRight: theme.spacing(2),
    },
    xIcon: {
      width: "11px",
      height: "11px",
      cursor: "pointer",
      color: theme.colors.gray500,
    },
  });

function MenuTag({customFields, tags, toggleTag}) {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const [searchQuery, setSearchQuery] = useState("");
  const {t} = useTranslation();

  const availableTags = useMemo(() => {
    const guestField = customFields?.["Guest"]?.find(
      (f) => f.field === "guest_tags",
    );
    return guestField?.data || [];
  }, [customFields]);

  const filteredTags = useMemo(() => {
    return availableTags.filter((tag) =>
      tag?.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [searchQuery, availableTags]);

  return (
    <Container className={classes.menuContainer}>
      <TextInput
        inputBase={OutlinedInputBase}
        startIcon={
          <SvgIcon
            component={ICONS.search}
            className={classes.searchIcon}
            sx={{color: theme.colors.gray500}}
          />
        }
        sx={{padding: theme.spacing(1, 3.5), marginBottom: theme.spacing(2)}}
        inputProps={{
          placeholder: t("inbox.guest-details.tags.search"),
          value: searchQuery,
          onChange: (e) => setSearchQuery(e.target.value),
        }}
        actions={
          !!searchQuery && (
            <SvgIcon
              component={ICONS.x}
              className={classes.xIcon}
              onClick={() => setSearchQuery("")}
              sx={{color: theme.colors.gray500}}
            />
          )
        }
      />

      {filteredTags.length > 0 ? (
        <ListComponent
          listStyles={{maxHeight: "300px", overflowY: "auto"}}
          gap={"0px"}
        >
          {filteredTags.map((entry, index) => {
            const normalizedEntry = entry.trim().toLowerCase();
            const isChecked = tags.some(
              (tag) => tag.trim().toLowerCase() === normalizedEntry,
            );

            return (
              <ListItemContainer
                key={index}
                onClick={() => toggleTag(entry)}
                itemProps={{
                  sx: {
                    backgroundColor: isChecked
                      ? theme.colors.gray50
                      : "transparent",
                    borderRadius: 0,
                  },
                }}
              >
                <Text as="div" variant="textSM" weight="medium" color="gray700">
                  {entry}
                </Text>
                <FlexContainer alignItems="center" sx={{height: "100%"}}>
                  <CheckBox
                    isChecked={isChecked}
                    onChange={() => toggleTag(entry)}
                  />
                </FlexContainer>
              </ListItemContainer>
            );
          })}
        </ListComponent>
      ) : (
        <FlexContainer
          flexDirection={"column"}
          alignItems={"center"}
          justifyContent={"center"}
          gap={theme.spacing(2)}
          sx={{height: "300px"}}
        >
          <Text color={"textSecondary"} variant={"textMD"}>
            {t("inbox.guest-details.tags.no-guest-tags-found")}
          </Text>
          <ButtonText
            buttonBase={OutlinedButtonBase}
            label={t("inbox.guest-details.tags.add-tag")}
            onClick={() => {
              if (searchQuery.trim() && !tags.includes(searchQuery)) {
                toggleTag(searchQuery);
                setSearchQuery("");
              }
            }}
          ></ButtonText>
        </FlexContainer>
      )}
    </Container>
  );
}

export default function GuestDetailsTags({guest, updateGuest}) {
  const {theme} = useTheme();
  const dispatch = useDispatch();
  const {customFields, setCustomFields} = useContext(InboxContext);
  const [addedTag, setAddedTag] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [localTags, setLocalTags] = useState(guest?.tags || []);
  const {t} = useTranslation();

  const updateCustomFields = () => {
    if (Object.keys(customFields).length) return;
    let isMounted = true;
    dispatch(
      getCustomFields({
        objects: [],
        onSuccess: (response) => {
          return !isMounted ? null : setCustomFields(response ?? {});
        },
      }),
    );
    return () => {
      isMounted = false;
    };
  };
  React.useEffect(updateCustomFields, []);

  useEffect(() => {
    if (guest?.tags) {
      setLocalTags(guest.tags);
    }
  }, [guest?.tags]);

  const handleChipClick = (tag) => {
    const updatedTags = localTags.filter((t) => t !== tag);
    setLocalTags(updatedTags);
    updateGuest(updatedTags);
  };

  const toggleTag = (tag) => {
    let updatedTags;
    if (localTags.includes(tag)) {
      updatedTags = localTags.filter((t) => t !== tag);
    } else {
      updatedTags = [...localTags, tag];
    }
    setLocalTags(updatedTags);
    updateGuest(updatedTags);
  };

  const menuItems = useMemo(
    () => [
      {
        Component: MenuTag,
        customFields,
        tags: localTags,
        addedTag,
        setAddedTag,
        toggleTag,
      },
    ],
    [customFields, localTags, addedTag, toggleTag],
  );

  return (
    <FlexContainer
      flexDirection="row"
      alignItems={"flex-start"}
      justifyContent="space-between"
      width="100%"
    >
      <FlexContainer
        flexDirection="row"
        alignItems="center"
        gap={theme.spacing(1)}
      >
        <SvgIcon
          component={ICONS.tag}
          sx={{
            color: theme.colors.textSecondary,
            width: "14px",
            height: "14px",
          }}
        />
        <Text variant={"textSM"} color={"textSecondary"} weight="medium">
          {t("inbox.guest-details.tags.label")}
        </Text>
      </FlexContainer>
      {guest?.tags === undefined ? (
        <Skeleton width="50%" />
      ) : (
        <>
          <ChipRow
            wrap
            sx={{maxWidth: "70%"}}
            align={"right"}
            chips={[
              ...localTags.map((tag) => ({
                Component: ChipText,
                text: tag,
                typographyVariant: "textXS",
                color: "gray700",
                chipBase: XChipBase,
                XColor: theme.colors.gray500,
                onXClick: () => handleChipClick(tag),
                sx: {
                  cursor: "pointer",
                  backgroundColor: theme.colors.gray100,
                  color: theme.colors.gray700,
                  "&:hover": {
                    backgroundColor: theme.colors.gray200,
                  },
                },
              })),
              {
                Component: ChipText,
                text: (
                  <SvgIcon
                    component={ICONS.plus}
                    sx={{width: "14px", height: "14px", padding: "1px"}}
                  />
                ),
                typographyVariant: "textXS",
                color: "gray700",
                chipBase: ChipBase,
                onClick: (event) => {
                  setAnchorEl(event.currentTarget);
                },
                sx: {
                  cursor: "pointer",
                  backgroundColor: theme.colors.gray100,
                  color: theme.colors.gray700,
                  padding: theme.spacing(1),
                  "&:hover": {
                    backgroundColor: theme.colors.gray200,
                  },
                },
              },
            ]}
          />
          <ContextMenu
            menuItems={menuItems}
            open={!!anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorEl={anchorEl}
          />
        </>
      )}
    </FlexContainer>
  );
}
